import { fixedUi } from '@/i18n/fixedUi';

/** 可直接展示给用户的业务文案（保留原文） */
const USER_FACING_PATTERNS = [
  /IPE豆不足/i,
  /积分不足/i,
  /容量不足/i,
  /平台存储空间不足/i,
  /请先登录/i,
  /未登录/i,
  /已注销/i,
  /已停用/i,
  /侵权/i,
  /知识产权/i,
  /违规/i,
  /版权/i,
  /Logo/i,
  /AI 模型认为/i,
  /无法解析/i,
  /请选择/i,
  /不能为空/i,
  /缺少/i,
  /没有需要重试/i,
  /任务无效/i,
  /不支持文件夹/i,
  /请拖拽/i,
  /请上传/i,
  /主图创作/i,
  /印花图/i,
  /参考图/i,
  /目标图/i,
  /最多/i,
  /至少/i,
  /格式/i,
  /文件过大/i,
  /图片过大/i,
  /任务已终止/i,
  /会话已过期/i,
  /未返回有效/i,
  /未产出/i,
  /重试完成/i,
  /SHEIN/i,
  /TEMU|Temu|openapi-b-partner|kuajingmaihuo|PA 网关/i,
  /\/open-api\//i,
  /ipeTrace=/i,
  /platformTrace=/i,
  /错误 code=/i,
  /平台 code=/i,
  /empty size value/i,
  /Does not currently support/i,
  /openapi00003/i,
  /没有该接口访问权限/i,
  /开发者后台/i,
  /图片上传/i,
  /图片处理/i,
  /upload-pic/i,
  /spu-info/i,
  /check-publish-permission/i,
  /不能为空/i,
  /attribute-template|属性模板|属性库|类目属性/i,
  /任务编号已存在/i,
  /店铺授权/i,
  /无权管理该店铺/i,
  /当前工作空间无权/i,
  /平台店铺/i,
  /已绑定/i,
  /商家 ID/i,
  /不能再授权/i,
  /auth_code/i,
  /Partner Center/i,
  /填写授权码|应用授权/i,
  /同步发品资格|同步店铺|同步售卖站点|同步可发类目|同步品牌|同步商家仓库|同步 IP/i,
  /今日手动同步|手动同步已达上限/i,
  /不支持商家仓库|不支持退货单|全托管店铺不支持/i,
  /查询上架|处置数据|发品资格|不可发品|上架信息/i,
];

/** 技术错误 → 用户友好文案（含中文业务语义时勿误伤） */
const TECHNICAL_RULES = [
  { test: /Transaction silently rolled back|rollback-only/i, message: '操作未完成，请查看失败模块详情后重试' },
  { test: /^(?=.*(502|503|504|500|501|BAD_GATEWAY|Service Unavailable|Internal Server Error))(?!.*[\u4e00-\u9fff]).*$/i, message: '服务器繁忙，请稍后再试' },
  { test: /timeout|超时|AbortError|ETIMEDOUT/i, message: '请求超时，请稍后重试' },
  { test: /network|Network Error|fetch failed|连接被重置|ECONNRESET|ECONNREFUSED|socket/i, message: '网络异常，请检查网络后重试' },
  { test: /DashScope|模型调用失败|api\.dashscope|(?:openai|qwen).*(?:fail|error|timeout|异常)|(?:fail|error|timeout|异常).*(?:openai|qwen)/i, message: '服务暂时不可用，请稍后再试' },
  { test: /^(?=.*(HTTP\s*\d{3}|请求失败\s*\(\d{3}\)))(?!.*[\u4e00-\u9fff]).*$/i, message: '服务器繁忙，请稍后再试' },
  { test: /NullPointerException|SQLException|IOException|at com\.|at org\.|at java\./i, message: '操作失败，请稍后重试' },
  { test: /unexpected token|SyntaxError|TypeError|ReferenceError|is not defined/i, message: '操作失败，请稍后重试' },
];

/** 需脱敏的系统级 HTTP 状态码 */
const SYSTEM_HTTP_STATUSES = new Set([404, 405, 500, 501, 502, 503, 504]);

/** 保留原始业务语义、不走系统脱敏的错误码 */
const PASS_THROUGH_ERROR_CODES = new Set([
  'LOGIN_REQUIRED',
  'UNAUTHORIZED',
  'CREDITS_INSUFFICIENT',
  'ACCOUNT_DEACTIVATED',
  'ACCOUNT_SUSPENDED',
  'SMS_CODE_INVALIDATED',
  'STORAGE_INSUFFICIENT',
  'STORAGE_PLATFORM_FULL',
  'TIMEOUT',
  'TOO_MANY_REQUESTS',
  'DUPLICATE_TASK_ID',
  'SHEIN_WORKSPACE_EXPIRED',
  'SHEIN_SHOP_AUTH_EXPIRED',
  'SHEIN_PASSWORD_INVALID',
  'SHEIN_MANUAL_SYNC_LIMIT',
  'TEMU_WORKSPACE_EXPIRED',
  'TEMU_SHOP_AUTH_EXPIRED',
  'TEMU_API_ERROR',
  'SHOP_BINDING_CONFLICT',
]);

function isUserFacingMessage(message) {
  const s = String(message || '').trim();
  if (!s) return false;
  return USER_FACING_PATTERNS.some((re) => re.test(s));
}

function matchTechnicalMessage(message) {
  const s = String(message || '').trim();
  if (!s) return '';
  for (const { test, message: mapped } of TECHNICAL_RULES) {
    if (test.test(s)) return mapped;
  }
  return '';
}

function looksTechnical(message) {
  const s = String(message || '').trim();
  if (!s) return false;
  if (matchTechnicalMessage(s)) return true;
  if (/["'][^{}[\]]{20,}["']/.test(s) && /\d{3}/.test(s)) return true;
  if (/^[A-Za-z0-9_\-:.\s"'，。；：]+$/.test(s) && s.length > 24) return true;
  return false;
}

function withContactHint(message) {
  const s = String(message || '').trim();
  if (!s || s.includes('联系客服')) return s;
  return `${s}；如问题持续，请联系客服`;
}

export function isSystemHttpStatus(status) {
  const code = Number(status);
  return Number.isFinite(code) && (SYSTEM_HTTP_STATUSES.has(code) || code >= 500);
}

export function defaultMessageForHttpStatus(status) {
  const code = Number(status);
  if (!Number.isFinite(code)) return fixedUi('操作失败，请稍后重试');
  if (code === 404) return fixedUi('请求的资源不存在');
  if (code === 405) return fixedUi('请求方式不允许，请稍后重试');
  if (code === 429) return fixedUi('操作过于频繁，请稍后再试');
  if (code === 401) return fixedUi('请先登录后再使用此功能');
  if (code === 403) return fixedUi('暂无权限执行此操作');
  if (isSystemHttpStatus(code)) return fixedUi('服务器繁忙，请稍后再试');
  if (code >= 400) return fixedUi('操作失败，请稍后重试');
  return fixedUi('操作失败，请稍后重试');
}

export function shouldPassThroughError(error) {
  if (!error) return false;
  if (error.code && PASS_THROUGH_ERROR_CODES.has(String(error.code))) {
    return true;
  }
  const status = Number(error.status);
  if (status === 403) {
    const msg = String(error.message || '');
    if (/IPE豆不足|积分不足|容量不足|平台存储空间不足|已注销|已停用|店铺授权|无权管理该店铺|当前工作空间无权|SHEIN 工作区未验证|TEMU 工作区|不可操作|请返回门禁|openapi00003|没有该接口访问权限|goods-certificates/.test(msg)) {
      return true;
    }
  }
  return false;
}

export function unwrapSpringStatusMessage(rawMessage) {
  const s = String(rawMessage || '').trim();
  if (!s) return '';
  const quoted = s.match(/\d{3}\s+[A-Z_]+\s+"([^"]+)"/);
  if (quoted) return quoted[1].trim();
  return s;
}

export function sanitizeIpeUserMessage(rawMessage, defaultMessage = '操作失败，请稍后重试') {
  const msg = unwrapSpringStatusMessage(rawMessage) || String(rawMessage || '').trim();
  if (!msg) return fixedUi(defaultMessage);

  // 店铺绑定冲突：文案长且含「解绑 / 重取 code」处置路径，必须完整透出
  if (isShopBindingConflictMessage(msg) && !looksTechnicalStack(msg)) {
    return msg.length <= 500 ? msg : `${msg.slice(0, 500)}…`;
  }

  // SHEIN/业务文案优先保留（即便文案里带「超时」也不要吞成笼统提示）
  if (isUserFacingMessage(msg) && !looksTechnicalStack(msg)) {
    return msg.length <= 300 ? msg : `${msg.slice(0, 300)}…`;
  }

  const technical = matchTechnicalMessage(msg);
  if (technical) return technical;

  if (looksTechnical(msg)) {
    return withContactHint(defaultMessage);
  }

  if (/[\u4e00-\u9fff]/.test(msg) && msg.length <= 200 && !/\b(Exception|NullPointer|at com\.)\b/.test(msg)) {
    return msg;
  }

  if (msg.length > 100 || /[{}[\]]/.test(msg)) {
    return withContactHint(defaultMessage);
  }

  return msg;
}

function isShopBindingConflictMessage(message) {
  const s = String(message || '');
  return /平台店铺|已绑定|商家 ID|不能再授权|auth_code|Partner Center|回收站/.test(s);
}

function looksTechnicalStack(message) {
  const s = String(message || '');
  return /NullPointerException|SQLException|at com\.|at org\.|at java\./i.test(s);
}

/** Temu/SHEIN 等平台常返回英文业务句，勿当成技术栈脱敏成「服务器繁忙」 */
function looksLikePlatformBusinessEnglish(message) {
  const s = String(message || '').trim();
  if (!s || s.length > 500 || looksTechnicalStack(s)) return false;
  if (/Exception|at com\.|Internal Server Error|BAD_GATEWAY/i.test(s)) return false;
  return /empty size|size value|category configuration|does not currently support|please check|try to submit|required|invalid|not support|permission|access|token|size chart|template/i.test(s);
}

export function toIpeUserErrorMessage(error, defaultMessage = '操作失败，请稍后重试') {
  if (!error) return fixedUi(defaultMessage);

  if (error.code === 'TIMEOUT' || error?.name === 'AbortError') {
    return '请求超时，请稍后重试';
  }
  if (error.code === 'LOGIN_REQUIRED' || error.code === 'UNAUTHORIZED') {
    return sanitizeIpeUserMessage(error.message, '请先登录后再使用此功能');
  }
  if (error.code === 'SHEIN_WORKSPACE_EXPIRED') {
    return sanitizeIpeUserMessage(error.message, 'SHEIN 工作区未验证或已过期，请重新输入密码');
  }
  if (shouldPassThroughError(error)) {
    return sanitizeIpeUserMessage(error.message, defaultMessage);
  }

  const status = Number(error.status);
  const rawPreview = String(error.message || '').trim();
  // 空 body 的 401/403 常见文案「请求失败(xxx)」会被技术规则误映射为「服务器繁忙」
  if ((status === 401 || status === 403) && /^请求失败\s*\(\d{3}\)$/.test(rawPreview)) {
    return defaultMessageForHttpStatus(status);
  }
  if (status === 401) {
    return sanitizeIpeUserMessage(error.message, '请先登录后再使用此功能');
  }
  if (isSystemHttpStatus(status)) {
    // 平台 / SHEIN / TEMU / 中文业务错误保留原文（含英文平台 errorMsg）
    if (
      rawPreview
      && !looksTechnicalStack(rawPreview)
      && (
        isUserFacingMessage(rawPreview)
        || /SHEIN|TEMU|Temu|\/open-api\/|错误 code=|平台 code=/i.test(rawPreview)
        || /[\u4e00-\u9fff]/.test(rawPreview)
        || looksLikePlatformBusinessEnglish(rawPreview)
      )
    ) {
      return sanitizeIpeUserMessage(rawPreview, defaultMessageForHttpStatus(status));
    }
    return defaultMessageForHttpStatus(status);
  }

  const raw =
    (error.message && String(error.message).trim())
    || (error.response?.data?.error && String(error.response.data.error).trim())
    || (error.response?.data?.message && String(error.response.data.message).trim())
    || String(error).trim();

  const fallback = Number.isFinite(status) ? defaultMessageForHttpStatus(status) : defaultMessage;
  return sanitizeIpeUserMessage(raw, fallback);
}
